import React, { useEffect, useState, useCallback } from "react";
import { TimerOff, Download, LogIn, Sunrise } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { StaffAvatar, StaffLabel } from "./StaffAvatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Skeleton } from "./ui/skeleton";
import { api, errMsg, downloadBlob, fmtTimeIST, istDateISO } from "../lib/api";
import { FetchButton, FetchForm, FetchPrompt, useFetchGate } from "./FetchBar";
import { ZoneSelect } from "./ZoneSelect";

// Idle History Report (Phase 69) — day-wise idle/no-signal totals per agent,
// computed server-side from the raw GPS trail with the tenant's configured threshold.

const fmtDur = (min) => {
  const m = Math.max(parseInt(min, 10) || 0, 0);
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
};
const cellTone = (m) => (m <= 0 ? "bg-muted" : m < 60 ? "bg-amber-200" : m < 180 ? "bg-amber-400" : "bg-red-400");
const dayLabel = (d) => {
  try {
    return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

export const IdleHistorySection = ({ code, canExport = true }) => {
  const [date, setDate] = useState(istDateISO());
  const [zoneId, setZoneId] = useState("");          // Phase 137: only staff mapped to this zone
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Phase 133: the report runs only on Fetch, for the date (+ zone) applied at that moment
  const gate = useFetchGate({ date, zoneId });
  const appliedDate = gate.applied?.date;
  const appliedZone = gate.applied?.zoneId || "";

  const load = useCallback(() => {
    if (!appliedDate) return;
    setLoading(true);
    api.get(`/t/${code}/tracking/idle-report`, { params: { date: appliedDate, ...(appliedZone ? { zone_id: appliedZone } : {}) } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)))
      .finally(() => setLoading(false));
  }, [code, appliedDate, appliedZone]);
  useEffect(() => { if (gate.fetched) load(); }, [load, gate.fetched, gate.version]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const q = `date=${appliedDate}${appliedZone ? `&zone_id=${encodeURIComponent(appliedZone)}` : ""}`;
      await downloadBlob(`/t/${code}/tracking/idle-report/export?${q}`, `idle-report-${appliedDate}${data?.zone ? `-${data.zone.zone_name}` : ""}.xlsx`);
      toast.success("Idle report downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="shadow-sm" data-testid="idle-history-section">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <TimerOff className="h-4 w-4" /> Idle History
            </CardTitle>
            <CardDescription>
              Day-wise idle &amp; no-signal totals per agent
              {data ? ` (threshold ${fmtDur(data.threshold)})` : ""} — computed from the GPS trail.
              {data?.zone && (
                <span className="block mt-0.5" data-testid="idle-report-zone-summary">
                  Zone <span className="font-medium">{data.zone.zone_name}</span> — {data.staff_count} staff member{data.staff_count === 1 ? "" : "s"} mapped
                  (collectors by ward territory, supervisors by zone).
                </span>
              )}
              {data?.duty_start && (
                <span className="block mt-0.5" data-testid="idle-report-signin-summary">
                  First sign-in shown in IST; duty hours{" "}
                  <span className="font-medium tabular-nums" data-testid="idle-report-duty-hours" data-duty-end={data.duty_end || ""}>
                    {data.duty_end ? `${data.duty_start}–${data.duty_end}` : `from ${data.duty_start} (no end time set)`}
                  </span>
                  {" — "}
                  <span className="font-medium">{data.signed_in_count} signed in</span>
                  {data.late_count > 0 && <>, <span className="font-medium text-amber-700">{data.late_count} late start{data.late_count === 1 ? "" : "s"}</span></>}
                  {data.duty_end && <span className="block text-[11px]">Stillness and signal gaps after {data.duty_end} IST are not counted.</span>}
                </span>
              )}
            </CardDescription>
          </div>
          <FetchForm onFetch={gate.fetch} className="flex items-center gap-2 flex-wrap" testId="idle-report-filter-form">
            <ZoneSelect code={code} value={zoneId} onChange={setZoneId} testId="idle-report-zone-select" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="w-[160px]" data-testid="idle-report-date-input" />
            <FetchButton type="submit" size="sm" className="h-9 min-w-0" loading={loading} dirty={gate.dirty} fetched={gate.fetched} disabled={!date} testId="idle-report-fetch-button" />
            {canExport && <Button type="button" variant="outline" size="sm" onClick={exportXlsx} disabled={exporting || loading || !gate.fetched}
              data-testid="idle-report-export-button">
              <Download className="h-3.5 w-3.5 mr-1.5" /> {exporting ? "Exporting..." : "Excel"}
            </Button>}
          </FetchForm>
        </div>
      </CardHeader>
      <CardContent>
        {!gate.fetched ? (
          <FetchPrompt onFetch={gate.fetch} testId="idle-report-fetch-prompt" compact
            subtitle="Pick the day above, then click Fetch to compute idle and no-signal totals from the GPS trail." />
        ) : loading && !data ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
          </div>
        ) : !data || data.agents.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="idle-report-empty">
            {data?.zone
              ? `No active field staff are mapped to ${data.zone.zone_name}. Map a collector's wards or a supervisor's zones under Users & Staff, or pick All zones.`
              : "No active field staff to report on."}
          </p>
        ) : (
          <>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Agent</TableHead>
                    <TableHead title="First sign-in of the day (IST)">First sign-in</TableHead>
                    <TableHead className="text-right" title="GPS pings that day (in brackets: pings inside duty hours)">Pings</TableHead>
                    <TableHead className="text-right">Idle</TableHead>
                    <TableHead className="text-right">No signal</TableHead>
                    <TableHead className="text-right">Episodes</TableHead>
                    <TableHead className="text-right">Total flagged</TableHead>
                    <TableHead>7-day pattern</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.agents.map((a) => (
                    <TableRow key={a.id} data-testid={`idle-report-row-${a.id}`}
                      className={a.ping_count === 0 ? "opacity-60" : ""}>
                      <TableCell className="text-sm">
                        <StaffAvatar code={code} userId={a.id} name={a.name} size="xs" className="mr-2 inline-flex align-middle" />
                        <span className="font-medium align-middle"><StaffLabel code={code} userId={a.id} name={a.name} empCode={a.employee_id} /></span>
                        {a.role === "supervisor" && (
                          <Badge variant="outline" className="ml-1.5 text-[10px] bg-teal-50 text-teal-700 border-teal-200">
                            Supervisor
                          </Badge>
                        )}
                        {a.ping_count === 0 && (
                          <p className="text-[11px] text-muted-foreground">Off duty — no GPS pings</p>
                        )}
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap" data-testid={`idle-report-signin-${a.id}`} data-late={a.late ? "true" : "false"}>
                        {a.first_seen_at ? (
                          <span className="inline-flex items-center gap-1.5">
                            <LogIn className="h-3 w-3 text-muted-foreground" />
                            <span className="tabular-nums">{fmtTimeIST(a.first_seen_at)}</span>
                            {a.late && (
                              <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-300 gap-0.5"
                                data-testid={`idle-report-late-${a.id}`}>
                                <Sunrise className="h-2.5 w-2.5" /> Late
                              </Badge>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground" title="No sign-in recorded for this day">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm" data-testid={`idle-report-pings-${a.id}`}>
                        {a.ping_count}
                        {data.duty_end && a.duty_ping_count !== undefined && a.duty_ping_count !== a.ping_count && (
                          <span className="text-muted-foreground text-[11px]" title={`${a.duty_ping_count} inside duty hours`}> ({a.duty_ping_count})</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {a.idle_minutes > 0
                          ? <span className="font-medium text-amber-700 tabular-nums">{fmtDur(a.idle_minutes)}</span>
                          : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right text-sm">
                        {a.no_signal_minutes > 0
                          ? <span className="font-medium text-red-700 tabular-nums">{fmtDur(a.no_signal_minutes)}</span>
                          : <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {(a.idle_episodes + a.no_signal_episodes) || "-"}
                      </TableCell>
                      <TableCell className="text-right text-sm font-semibold tabular-nums"
                        data-testid={`idle-report-total-${a.id}`}>
                        {a.flagged_minutes > 0 ? fmtDur(a.flagged_minutes) : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-end gap-1">
                          {data.days.map((d, i) => (
                            <div key={d} className="flex flex-col items-center gap-0.5"
                              title={`${dayLabel(d)}: ${a.week[i] > 0 ? fmtDur(a.week[i]) : "no flagged time"}`}
                              data-testid={`idle-week-cell-${a.id}-${d}`}>
                              <div className={`h-4 w-6 rounded-sm ${cellTone(a.week[i])} ${d === data.date ? "ring-2 ring-primary/60" : ""}`} />
                              <span className="text-[9px] text-muted-foreground">{dayLabel(d).split(" ")[0]}</span>
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground" data-testid="idle-report-legend">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-muted inline-block border" /> none</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-200 inline-block" /> under 1h</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-amber-400 inline-block" /> 1-3h</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-red-400 inline-block" /> over 3h</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
